import React, { useState, useEffect } from 'react';
import { DollarSign, Download, TrendingUp, Receipt } from 'lucide-react';
import axios from 'axios';

const ExamFeeReport = () => {
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    exam_id: '' 
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ course_id: courseId, semester_id: '', exam_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setReportData(null);
  };

  const generateReport = async () => {
    if (!filters.exam_id) {
      alert('Please select an Exam');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/examination/exam-fee-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            exam_id: filters.exam_id
          }
        }
      );

      setReportData(response.data);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    alert('Export to Excel functionality will be implemented');
  };

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Fee Collection Report</h1>
          <p className="text-sm text-gray-500 mt-1">View exam fee collection statistics and details</p>
        </div>
        {reportData && (
          <button 
            onClick={exportReport}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export Report
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id} 
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">All Semesters</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.exam_id} 
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
            >
              <option value="">Select Exam</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name} - ₹{exam.exam_fee || 0}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateReport}
              disabled={!filters.exam_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Collected</p>
                <p className="text-3xl font-bold text-green-600 mt-1">₹{reportData.total_collected.toLocaleString('en-IN')}</p>
              </div>
              <DollarSign className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{reportData.total_students}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Transactions</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">{reportData.total_transactions}</p>
              </div>
              <Receipt className="h-12 w-12 text-purple-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg per Student</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">
                  ₹{reportData.total_students > 0 ? Math.round(reportData.total_collected / reportData.total_students) : 0}
                </p>
              </div>
              <DollarSign className="h-12 w-12 text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {reportData && Object.keys(reportData.by_payment_mode).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Collection by Payment Mode</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(reportData.by_payment_mode).map(([mode, amount]) => (
              <div key={mode} className="border rounded-lg p-4">
                <p className="text-sm text-gray-500">{mode}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">₹{amount.toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {reportData && reportData.payments.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 bg-blue-50 border-b">
            <h3 className="font-semibold text-blue-900">Payment Details</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.payments.map((payment) => (
                <tr key={payment.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{payment.receipt_no}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.payment_date}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.student_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{payment.admission_number}</td>
                  <td className="px-4 py-3 text-center text-sm font-semibold text-green-600">₹{payment.amount}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {payment.payment_mode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {payment.transaction_id || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : reportData ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Receipt className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No payments recorded for this exam yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select an exam to generate fee collection report</p>
        </div>
      )}
    </div>
  );
};

export default ExamFeeReport;
